import { Collection } from 'firestorter';

import CollectionName from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';

const ALL_CLIENTS_TAG = {
  id: 'ALL_CLIENTS',
  name: 'All Clients',
};

/**
 * Class representing an Announcement.
 * An announcement is a post that is shared to a group of clients.
 *
 * @class Announcement
 * @extends BaseDocument
 */
class Announcement extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.ANNOUNCEMENT}/${id}`, opts);
  }

  /**
   * The owner of the announcement.
   * @returns {string}
   */
  get coach() {
    return this.data.coach;
  }

  /**
   * The message of the announcement.
   * @returns {string}
   */
  get message() {
    return this.data.message;
  }

  /**
   * The id of the user tag associated with the announcement.
   * If empty, the announcement belongs to all clients.
   * @returns {string}
   */
  get tagId() {
    return this.data.tagId || '';
  }

  /**
   * The name of the user tag associated with the announcement.
   * @returns {string}
   */
  get tagName() {
    return this.data.tagName || '';
  }

  /**
   * The attachments data of the announcement.
   * @returns {Object[]}
   */
  get attachments() {
    return this.data.attachments || [];
  }

  /**
   * The number of likes on the announcement.
   * @returns {number}
   */
  get likes() {
    return this.data.likes || 0;
  }

  /**
   * The date and time the announcement was created.
   * @returns {Date}
   */
  get createdAt() {
    return this.data.createdAt;
  }

  /**
   * The user who created the announcement.
   * @returns {string}
   */
  get createdBy() {
    return this.data.createdBy;
  }

  /**
   * The date and time the announcement was last updated.
   * @returns {Date}
   */
  get lastUpdatedAt() {
    return this.data.lastUpdatedAt;
  }

  /**
   * The user who last updated the announcement.
   * @returns {string}
   */
  get lastUpdatedBy() {
    return this.data.lastUpdatedBy;
  }

  /**
   * Whether the announcement is removed.
   * @returns {boolean}
   */
  get isRemoved() {
    return !!this.data.isRemoved;
  }

  /**
 * Removes the announcement from firestore.
 * @returns {Promise<void>}
 */
  async remove() {
    return this.updateFields({ isRemoved: true });
  }

  /**
   * Updates the announcement in firestore.
   * @param {Object} data - JSON data to be attached to the new announcement doc
   * @returns {Promise<void>}
   */
  async updateDoc(data) {
    return this.updateFields(data);
  }

  /**
   * Creates a new announcement document in firestore.
   *
   * @param {Object} data - JSON data to be attached to the new announcement doc
   * @returns {Promise<string>} The id of the created firestore document
   */
  static async addDoc(data) {
    const announcementCollection = new Collection(CollectionName.ANNOUNCEMENT);
    const { id } = await announcementCollection.add(data);
    return id;
  }

  /**
   * Gets the announcements for a coach.
   *
   * @param {string} coachId - Id of the coach
   * @returns {Promise<Collection>} The collection of announcements from firestore
   */
  static async getAnnouncementsByCoach(coachId) {
    const announcementsCollection = new Collection(CollectionName.ANNOUNCEMENT, {
      createDocument: ({ id }, opts) => new Announcement(id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => ref
        .where('coach', '==', coachId)
        .where('isRemoved', '==', false)
        .orderBy('createdAt', 'desc'),
    });
    await announcementsCollection.fetch();
    return announcementsCollection;
  }

  /**
   * Gets an announcement by its id.
   *
   * @param {string} id - Id of the announcement
   * @returns {Promise<Announcement>} The announcement from firestore
   */
  static async getAnnouncementById(id) {
    const announcementDoc = new Announcement(id);
    await announcementDoc.init();
    return announcementDoc;
  }
}

export default Announcement;
export {
  ALL_CLIENTS_TAG,
};
